import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../lib/api';
import { PageHelp } from '../components/PageHelp';
import { formatUserName } from '../lib/format';
import { ROLE_LABELS, type AppUser, type Division } from '../lib/types';
import { EMPTY_USER_FILTER, filterUsers, type UserFilter } from '../lib/userFilter';
import type { Role } from '../lib/AuthContext';

interface FormState {
  email: string;
  givenName: string;
  familyName: string;
  role: Role;
  divisionIds: string[];
  kind: 'lokal' | 'myhours';
  /** nur lokal: Einladung per E-Mail (Cognito) oder Passwort anzeigen */
  sendEmail: boolean;
}

/** Zugangsdaten, die der Admin selbst weitergibt. */
interface ShownPassword {
  email: string;
  password: string;
}

const EMPTY_FORM: FormState = {
  email: '',
  givenName: '',
  familyName: '',
  role: 'abteilungsleiter',
  divisionIds: [],
  kind: 'myhours',
  sendEmail: true,
};

/**
 * FR-4: der Admin legt Benutzer per E-Mail an -- als "MyHours-Konto" (nur der Eintrag, die Person
 * meldet sich mit MyHours an) oder als lokales MyBudget-Konto. Für lokale Konten verschickt
 * Cognito die Einladung mit einem Einmal-Passwort, oder der Admin bekommt es angezeigt; beides
 * geht später auch als "Neues Passwort".
 */
export default function BenutzerPage() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [dialogError, setDialogError] = useState<string>();
  /** undefined = Dialog zu; null = neuer Benutzer; sonst die E-Mail des bearbeiteten. */
  const [editing, setEditing] = useState<string | null>();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [info, setInfo] = useState<string>();
  /** Benutzer, für den der Dialog "Neues Passwort" offen ist. */
  const [resetFor, setResetFor] = useState<AppUser>();
  const [shown, setShown] = useState<ShownPassword>();
  const [busy, setBusy] = useState(false);
  /** Benutzer, für den die Löschabfrage offen ist. */
  const [deleteFor, setDeleteFor] = useState<AppUser>();
  const [filter, setFilter] = useState<UserFilter>(EMPTY_USER_FILTER);
  const visibleUsers = filterUsers(users, filter);

  async function deleteUser(user: AppUser) {
    setBusy(true);
    setDialogError(undefined);
    try {
      await api.del(`/admin/users/${encodeURIComponent(user.email)}`);
      setDeleteFor(undefined);
      setInfo(`${formatUserName(user)} wurde gelöscht.`);
      await load();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  async function load() {
    const [u, d] = await Promise.all([
      api.get<AppUser[]>('/admin/users'),
      api.get<Division[]>('/admin/divisions'),
    ]);
    setUsers(u);
    setDivisions(d);
    setLoading(false);
  }

  useEffect(() => {
    load().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Benutzer konnten nicht geladen werden');
      setLoading(false);
    });
  }, []);

  const divisionName = (id: string) => divisions.find((d) => d.divisionId === id)?.name ?? id;

  function openCreate() {
    setForm(EMPTY_FORM);
    setDialogError(undefined);
    setEditing(null);
  }

  function openEdit(user: AppUser) {
    setForm({
      email: user.email,
      givenName: user.givenName ?? '',
      familyName: user.familyName ?? '',
      role: user.role,
      divisionIds: user.divisionIds,
      kind: user.kind,
      sendEmail: true,
    });
    setDialogError(undefined);
    setEditing(user.email);
  }

  async function handleSave() {
    setDialogError(undefined);
    setInfo(undefined);
    try {
      if (editing === null) {
        const created = await api.post<AppUser & { password?: string }>('/admin/users', form);
        if (created.password) setShown({ email: created.email, password: created.password });
        else if (form.kind === 'lokal') setInfo(`Einladung an ${created.email} verschickt.`);
      } else if (editing) {
        await api.put(`/admin/users/${encodeURIComponent(editing)}`, {
          role: form.role,
          divisionIds: form.divisionIds,
          givenName: form.givenName,
          familyName: form.familyName,
        });
      }
      setEditing(undefined);
      await load();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  async function newPassword(user: AppUser, sendEmail: boolean) {
    setBusy(true);
    setDialogError(undefined);
    try {
      const res = await api.post<{ password?: string; emailSent?: boolean }>(
        `/admin/users/${encodeURIComponent(user.email)}/password`,
        { sendEmail },
      );
      setResetFor(undefined);
      if (res.password) setShown({ email: user.email, password: res.password });
      else setInfo(`Neues Passwort per E-Mail an ${user.email} verschickt.`);
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Passwort konnte nicht gesetzt werden');
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(user: AppUser) {
    setError(undefined);
    try {
      await api.put(`/admin/users/${encodeURIComponent(user.email)}`, { active: !user.active });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Änderung fehlgeschlagen');
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const needsDivision = form.role === 'abteilungsleiter' && form.divisionIds.length === 0;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">Benutzer</Typography>
        <PageHelp text="Admin, Vorstand und Abteilungsleiter. Wer ein MyHours-Konto hat, meldet sich damit an; alle anderen bekommen ein eigenes MyBudget-Konto, die Zugangsdaten per E-Mail oder zum Weitergeben angezeigt. Über den Schlüssel vergeben Sie einem MyBudget-Konto ein neues Passwort. Änderungen an Rolle und Abteilungen wirken bei der nächsten Anmeldung." />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {info && (
        <Alert severity="success" onClose={() => setInfo(undefined)}>
          {info}
        </Alert>
      )}
      <Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
          Neuer Benutzer
        </Button>
      </Box>

      {/* Filter in einer Zeile oberhalb der Tabelle, wie im Dashboard */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'center' } }}
      >
        <TextField
          size="small"
          label="Suche (Name, E-Mail)"
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Rolle"
          value={filter.role}
          onChange={(e) => setFilter({ ...filter, role: e.target.value as UserFilter['role'] })}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">Alle Rollen</MenuItem>
          {(Object.keys(ROLE_LABELS) as Role[]).map((r) => (
            <MenuItem key={r} value={r}>
              {ROLE_LABELS[r]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Abteilung"
          value={filter.divisionId}
          onChange={(e) => setFilter({ ...filter, divisionId: e.target.value })}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">Alle Abteilungen</MenuItem>
          {divisions.map((d) => (
            <MenuItem key={d.divisionId} value={d.divisionId}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Anmeldung"
          value={filter.kind}
          onChange={(e) => setFilter({ ...filter, kind: e.target.value as UserFilter['kind'] })}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">Alle</MenuItem>
          <MenuItem value="myhours">MyHours-Konto</MenuItem>
          <MenuItem value="lokal">MyBudget-Konto</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Aktiv"
          value={filter.active}
          onChange={(e) => setFilter({ ...filter, active: e.target.value as UserFilter['active'] })}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Alle</MenuItem>
          <MenuItem value="aktiv">Aktiv</MenuItem>
          <MenuItem value="inaktiv">Inaktiv</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {visibleUsers.length} von {users.length}
        </Typography>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Abteilungen</TableCell>
              <TableCell>Anmeldung</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">Kein Benutzer passt zum Filter.</Typography>
                </TableCell>
              </TableRow>
            )}
            {visibleUsers.map((u) => (
              <TableRow key={u.email} hover>
                <TableCell>
                  <Typography variant="body2">{formatUserName(u)}</Typography>
                  {(u.givenName || u.familyName) && (
                    <Typography variant="caption" color="text.secondary">
                      {u.email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{ROLE_LABELS[u.role]}</TableCell>
                <TableCell>
                  {u.role === 'abteilungsleiter'
                    ? u.divisionIds.map((id) => (
                        <Chip key={id} label={divisionName(id)} size="small" sx={{ mr: 0.5 }} />
                      ))
                    : 'alle'}
                </TableCell>
                <TableCell>{u.kind === 'myhours' ? 'MyHours-Konto' : 'MyBudget-Konto'}</TableCell>
                <TableCell>
                  <Switch checked={u.active} onChange={() => void toggleActive(u)} />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {u.kind === 'lokal' && (
                    <Tooltip title="Neues Passwort">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDialogError(undefined);
                          setResetFor(u);
                        }}
                      >
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" onClick={() => openEdit(u)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDialogError(undefined);
                        setDeleteFor(u);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={editing !== undefined}
        onClose={() => setEditing(undefined)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editing === null ? 'Neuer Benutzer' : 'Benutzer bearbeiten'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="E-Mail-Adresse"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={editing !== null}
              required
              fullWidth
              autoFocus={editing === null}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Vorname"
                value={form.givenName}
                onChange={(e) => setForm({ ...form, givenName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Nachname"
                value={form.familyName}
                onChange={(e) => setForm({ ...form, familyName: e.target.value })}
                fullWidth
              />
            </Stack>
            <TextField
              select
              label="Rolle"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              fullWidth
            >
              {(Object.keys(ROLE_LABELS) as Role[]).map((r) => (
                <MenuItem key={r} value={r}>
                  {ROLE_LABELS[r]}
                </MenuItem>
              ))}
            </TextField>
            {form.role === 'abteilungsleiter' && (
              <TextField
                select
                label="Abteilungen"
                value={form.divisionIds}
                onChange={(e) =>
                  setForm({ ...form, divisionIds: e.target.value as unknown as string[] })
                }
                slotProps={{ select: { multiple: true } }}
                error={needsDivision}
                helperText={needsDivision ? 'Mindestens eine Abteilung wählen.' : undefined}
                fullWidth
              >
                {divisions
                  .filter((d) => d.active || form.divisionIds.includes(d.divisionId))
                  .map((d) => (
                    <MenuItem key={d.divisionId} value={d.divisionId}>
                      {d.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            {editing === null && (
              <Box>
                <FormLabel>Anmeldung</FormLabel>
                <RadioGroup
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as FormState['kind'] })}
                >
                  <FormControlLabel
                    value="myhours"
                    control={<Radio />}
                    label="Mit dem MyHours-Konto (gleiche E-Mail wie in MyHours)"
                  />
                  <FormControlLabel
                    value="lokal"
                    control={<Radio />}
                    label="Eigenes MyBudget-Konto"
                  />
                </RadioGroup>
              </Box>
            )}
            {editing === null && form.kind === 'lokal' && (
              <Box>
                <FormLabel>Zugangsdaten</FormLabel>
                <RadioGroup
                  value={form.sendEmail ? 'mail' : 'anzeigen'}
                  onChange={(e) => setForm({ ...form, sendEmail: e.target.value === 'mail' })}
                >
                  <FormControlLabel
                    value="mail"
                    control={<Radio />}
                    label="Per E-Mail verschicken (Einladung mit Einmal-Passwort)"
                  />
                  <FormControlLabel
                    value="anzeigen"
                    control={<Radio />}
                    label="Passwort anzeigen, ich gebe es selbst weiter"
                  />
                </RadioGroup>
              </Box>
            )}
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(undefined)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={!form.email.trim() || needsDivision}
            onClick={() => void handleSave()}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetFor)} onClose={() => setResetFor(undefined)} maxWidth="sm">
        <DialogTitle>Neues Passwort für {resetFor && formatUserName(resetFor)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              MyBudget vergibt ein neues Einmal-Passwort; das bisherige gilt dann nicht mehr. Bei
              der nächsten Anmeldung legt {resetFor?.email} ein eigenes Passwort fest.
            </Typography>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetFor(undefined)}>Abbrechen</Button>
          <Button disabled={busy} onClick={() => resetFor && void newPassword(resetFor, false)}>
            Passwort anzeigen
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => resetFor && void newPassword(resetFor, true)}
          >
            Per E-Mail senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteFor)} onClose={() => setDeleteFor(undefined)} maxWidth="sm">
        <DialogTitle>{deleteFor && formatUserName(deleteFor)} löschen?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              {deleteFor?.kind === 'lokal'
                ? 'Der Benutzer und sein MyBudget-Konto werden gelöscht. Das lässt sich nicht rückgängig machen; zum vorübergehenden Sperren genügt "Aktiv" auszuschalten.'
                : 'Der Benutzer verliert den Zugriff auf MyBudget. Sein MyHours-Konto bleibt unverändert; zum vorübergehenden Sperren genügt "Aktiv" auszuschalten.'}
            </Typography>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFor(undefined)}>Abbrechen</Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => deleteFor && void deleteUser(deleteFor)}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(shown)} onClose={() => setShown(undefined)} maxWidth="sm">
        <DialogTitle>Zugangsdaten</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              Bitte persönlich weitergeben. Das Passwort wird nur jetzt angezeigt; bei der ersten
              Anmeldung muss ein eigenes Passwort festgelegt werden.
            </Typography>
            <Typography variant="body2">
              Anmeldung: {window.location.origin}/login
              <br />
              E-Mail: {shown?.email}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '1.25rem' }}>
                {shown?.password}
              </Typography>
              <Tooltip title="Kopieren">
                <IconButton
                  size="small"
                  onClick={() => void navigator.clipboard?.writeText(shown?.password ?? '')}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShown(undefined)}>
            Fertig
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
